import { SMF_NO_TEXT_MESSAGE } from './constants';
import { DrEntity } from './dr-entity';
import { DocumentListTabType, TagScope } from './enums';
import { TagQueueItem } from './tag';
import { TaggedEntity } from './tagged-entity';

export enum DocumentFormat {
    NATIVE = 'NATIVE',
    HTML = 'HTML',
    HTML_HIGHLIGHTED = 'HTML_HIGHLIGHTED',
    METADATA = 'METADATA',
    TEXT = 'TEXT',
    PDF = 'PDF',
    IMAGE_PDF = 'IMAGE_PDF',
    HTML5_HIGHLIGHTED = 'HTML5_HIGHLIGHTED',
    REDACTABLE = 'REDACTABLE'
}

export class DrDocument extends DrEntity {
    filename: string;
    tagList: Array<TaggedEntity>;

    // off-model display values
    displayName: string;
    iconClass: string;
    iconColor: string;
    iconSecondClass: string;
    iconSecondColor: string;
    attachmentCount: number;
    isAttachment: boolean;
    hasFamily: boolean;
    hasThread: boolean;
    isThread: boolean;
    tagsTooltip: string;

    // attributes
    docnum: string;
    filetype: string;
    author: string;
    from: string;
    to: string;
    size: string;
    subject: string;
    dateprimary: string;
    datescanned: string;
    date_or_lastmodifiedtime: string;
    createdtime: string;
    docclass: string;
    attachments: string;
    parsingstatus: string;
    importpath: string;
    family_handle: string;
    thread_handle: string;
    sent: string;
    received: string;
    score: string;
    add_history_username: string;
    add_history_comments: string;
    add_history_time: string;
    percentCommonTerms: string;
    group: string;

    // export/review production specific attributes
    docid: string;
    volume: string;
    responsive: string;  // this is reason code.

    // grid attributes
    isParent: boolean;
    isChild: boolean;
    isExpanded: boolean;
    children: DrDocument[];
    level: number;

    // doc viewer attributes
    stored_image: string;
    hiddendata: string;
    trackchanges: boolean;
    docannotations: string;

    // smf document
    smfDataType: string;
    smfMessageBody: string;
    smfChatId: string;
    toName: string;
    fromName: string;

    threadType: string;

    constructor(value?: Partial<DrDocument>) {
        super(value);
        if (value) {
            if (value.tagList) {
                value.tagList = value.tagList.map(tag => new TaggedEntity(tag));
            }
            Object.assign(this, value);
            let toIdentifier = '';
            let fromIdentifier = '';
            if (value.attributes) {
                value.attributes.forEach(attribute => {
                    // attributes
                    if (attribute.name === 'docnum') { this.docnum = attribute.value; }
                    if (attribute.name === 'filetype') { this.filetype = attribute.value; }
                    if (attribute.name === 'author') { this.author = attribute.value; }
                    if (attribute.name === 'from') { this.from = attribute.value; }
                    if (attribute.name === 'to') { this.to = attribute.value; }
                    if (attribute.name === 'size') { this.size = attribute.value; }
                    if (attribute.name === 'subject') { this.subject = attribute.value; }
                    if (attribute.name === 'dateprimary') { this.dateprimary = attribute.value; }
                    if (attribute.name === 'datescanned') { this.datescanned = attribute.value; }
                    if (attribute.name === 'date_or_lastmodifiedtime') { this.date_or_lastmodifiedtime = attribute.value; }
                    if (attribute.name === 'createdtime') { this.createdtime = attribute.value; }
                    if (attribute.name === 'docclass') { this.docclass = attribute.value; }
                    if (attribute.name === 'attachments') { this.attachments = attribute.value; }
                    if (attribute.name === 'parsingstatus') { this.parsingstatus = attribute.value; }
                    if (attribute.name === 'importpath') { this.importpath = attribute.value; }
                    if (attribute.name === 'hasfamily') { this.hasFamily = attribute.value === 'true'; }
                    if (attribute.name === 'family_handle') { this.family_handle = attribute.value; }
                    if (attribute.name === 'thread_handle') { this.thread_handle = attribute.value; }
                    if (attribute.name === 'score') {
                        const scoreValue = parseFloat(attribute.value);
                        this.score = isNaN(scoreValue) ? '0' : scoreValue.toFixed(2);
                    }
                    if (attribute.name === 'sent') { this.sent = attribute.value; }
                    if (attribute.name === 'received') { this.received = attribute.value; }
                    if (attribute.name === 'stored_image') { this.stored_image = attribute.value; }
                    if (attribute.name === 'hiddendata') { this.hiddendata = attribute.value; }
                    if (attribute.name === 'trackchanges') { this.trackchanges = attribute.value === 'true'; }
                    if (attribute.name === 'docannotations') { this.docannotations = attribute.value; }
                    if (attribute.name === 'add_history_username') { this.add_history_username = attribute.value; }
                    if (attribute.name === 'add_history_comments') { this.add_history_comments = attribute.value; }
                    if (attribute.name === 'add_history_time') { this.add_history_time = attribute.value; }
                    if (attribute.name === 'percentCommonTerms') {
                        const percentCommonTermsValue = parseFloat(attribute.value);
                        this.percentCommonTerms = isNaN(percentCommonTermsValue) ? '0' : percentCommonTermsValue.toFixed(2);
                    }
                    if (attribute.name === 'group') { this.group = attribute.value; }
                    // export/production document specific attributes
                    if (attribute.name === 'docid') { this.docid = attribute.value; }
                    if (attribute.name === 'volume') { this.volume = attribute.value; }
                    if (attribute.name === 'responsive') { this.responsive = attribute.value; }

                    // smf thread specific attributes
                    if (attribute.name === 'smf_message_body') { this.smfMessageBody = attribute.value; }
                    if (attribute.name === 'to_name') { this.toName = attribute.value; }
                    if (attribute.name === 'from_name') { this.fromName = attribute.value; }
                    if (attribute.name === 'to_identifier') { toIdentifier = attribute.value; }
                    if (attribute.name === 'from_identifier') { fromIdentifier = attribute.value; }
                    if (attribute.name === 'smf_datatype' && this.filetype === 'email') {
                        this.smfDataType = attribute.value;
                        this.filetype = this.smfDataType.includes('Cellebrite_ChatMessage') ? 'smf' : this.filetype;  
                    }
                });
            }

            if (value.smfDataType?.includes('Cellebrite_ChatMessage') && this.filetype !== 'smf') {
                this.filetype = 'smf';
            }
            if (this.filetype === 'smf') {
                if (!this.toName) {
                    this.toName = toIdentifier;
                }
                if (!this.fromName) {
                    this.fromName = fromIdentifier;
                }
                this.smfMessageBody = this.smfMessageBody && this.smfMessageBody !== '' ? this.smfMessageBody : SMF_NO_TEXT_MESSAGE;
                if (this.smfMessageBody === SMF_NO_TEXT_MESSAGE) {
                }
                this.displayName = this.smfMessageBody;
            } else {
                this.displayName = this.filename || this.subject;
            }
            this.author = this.author || this.from;
            this.isAttachment = ['Message_Attachment', 'Message_OLE_Attachment', 'EDoc_OLE_Attachment'].includes(this.docclass);
            this.attachmentCount = this.attachments ? this.attachments.split(';').length : 0;
            this.hasThread = !!this.thread_handle;
            if (this.filetype === 'smf' && (this.threadType === 'EMAIL_THREAD' || this.threadType === 'ORPHANED_THREAD')) {
                this.iconClass = 'fa-comments-o';
                this.iconColor = 'color-blue-grey-03';
                this.iconSecondClass = 'fa-link ';
                this.iconSecondColor = 'color-blue-01';
                this.isThread = true;
            } else if (this.threadType === 'EMAIL_THREAD' || this.threadType === 'ORPHANED_THREAD') {
                this.iconClass = 'fa-envelope';
                this.iconColor = 'color-blue-grey-03';
                this.iconSecondClass = 'fa-link ';
                this.iconSecondColor = 'color-blue-01';
                this.isThread = true;
            } else if (this.filetype === 'email' || this.filetype === 'message' || this.filetype === 'Email Message' || this.filetype === 'Microsoft Outlook Email Message') {
                this.iconClass = 'fa-envelope color-blue-01';
            }
            else if (this.filetype === 'smf') {
                this.iconClass = 'fa-comments-o color-blue-01';
            } else if (this.filetype === 'directory') {
                this.iconClass = 'fa-folder color-tan-07';
            } else if (this.isAttachment) {
                this.iconClass = 'fa-paperclip color-gray-10';
            } else {
                this.iconClass = 'fa-file-text-o';
            }

            if (this.tagList && this.tagList.length > 3) {
                this.tagsTooltip = '';
                for (let i = 0; i < this.tagList.length; i++) {
                    this.tagsTooltip += this.tagList[i].name + (i < this.tagList.length - 1 ? '\x0A' : '');
                }
            }
        }
    }
}

export class DocumentViewerContext {
    selectedDocument: DrDocument;
    documentListTab: DocumentListTabType;
    displayFormat: DocumentFormat;
    startIndex: number;
    hideDuplicates: boolean;
    sortBy: string;
    sortAscending: boolean;
    allMetadataFields: boolean;
    hideEmptyMetadata: boolean;
    findInDocument: string;
    zoom: number;
    rotation: number;
    tagQueue: Array<TagQueueItem>;
    tagComment: string;
    tagScope: TagScope;
    isGeneratedComGridsDocList = false;
    isShortMessageThreads = false;

    constructor(value?: Partial<DocumentViewerContext>) {
        if (value) {
            if (value.selectedDocument) {
                value.selectedDocument = new DrDocument(value.selectedDocument);
            }
            Object.assign(this, value);
        }
    }
}

export enum EmailMessageType {
    ROOT = 'ROOT',
    REPLY = 'REPLY',
    FORWARD = 'FORWARD'
}

export class EmailThreadDocument extends DrDocument {

    emailMessageType: EmailMessageType;
    viewMember: boolean;
    contained: boolean;
    childDocumentList: Array<EmailThreadDocument>;

    constructor(value?: Partial<EmailThreadDocument>) {
        super(value);
        if (value) {
            if (value.childDocumentList) {
                this.childDocumentList = this.childDocumentList.map(child => new EmailThreadDocument(child));
            }
        }
    }
}

export class MainEmailThreadDocument extends DrDocument {

    domainsInvolved: number;
    firstMessage: EmailThreadDocument;
    lastUpdate: string;
    numberDuplicatePosts: number;
    numberOfAttachments: number;
    numberOfDuplicateAttachments: any;
    numberOfUniqueAttachments: any;
    numberParticipants: number;
    numberAttachments: any;
    numberPosts: any;
    numberUniquePosts: number;
    originator: string;
    startDate: string;
    subject: string;
    threadType: string;
    taggedEntityList: Array<TaggedEntity>;
    tagList: Array<TaggedEntity>;

    constructor(value?: Partial<MainEmailThreadDocument>) {
        super(value);
        if (value) {
            if (value.smfDataType?.includes('Cellebrite')) {
                value.filetype = 'smf';
                value.smfMessageBody = (value.smfMessageBody && value.smfMessageBody !== '') ? value.smfMessageBody : SMF_NO_TEXT_MESSAGE;
            }
            if (value.taggedEntityList) {
                value.tagList = value.taggedEntityList.map(tag => new TaggedEntity(tag));
            }

            if (value.tagList && value.tagList.length > 3) {
                value.tagsTooltip = '';
                for (let i = 0; i < value.tagList.length; i++) {
                    value.tagsTooltip += value.tagList[i].name + (i < value.tagList.length - 1 ? '\x0A' : '');
                }
            }

            if (value.numberDuplicatePosts) {
                value.numberPosts = value.numberDuplicatePosts === 0 ? value.numberUniquePosts : value.numberUniquePosts + ' (' + value.numberDuplicatePosts + ')';
            }

            if (value.numberOfUniqueAttachments) {
                value.numberOfUniqueAttachments = value.numberOfUniqueAttachments === -1 ? 'N/A' : value.numberOfUniqueAttachments;
            }

            if (value.numberOfDuplicateAttachments) {
                value.numberOfDuplicateAttachments = value.numberOfDuplicateAttachments === -1 ? 'N/A' : value.numberOfDuplicateAttachments;
            }

            if (value.numberOfDuplicateAttachments) {
                value.numberAttachments = value.numberDuplicatePosts === 0 ? value.numberOfUniqueAttachments : value.numberOfUniqueAttachments + ' (' + value.numberOfDuplicateAttachments + ')';
            }

            Object.assign(this, value);
        }
    }
}

const metadataSearchDisabled = [
    '_handle',
    'coding_complete',
    'dafilesource',
    'export_view_dupe_priority',
    'export_view_reason_code',
    'handle',
    'importpath',
    'password',
    'recipientsmd5',
    'similarity',
    'size',
    'viewname',
];

export class DocumentViewerMetadataField {
    key: string;
    value: string;
    displayValue: string;
    keyTooltip: string;
    displayValueTooltip: string;
    isParent: boolean;
    isExpanded: boolean;
    children: DocumentViewerMetadataField[];
    level = 0;
    isButtonVisible = false;
    isButtonEnabled = false;
    buttonTooltip: string;
    buttonIconClass = 'fa-search';
    buttonIconColor = 'color-yellow-07';

    constructor(value?: Partial<DocumentViewerMetadataField>) {
        if (value) {
            Object.assign(this, value);
            this.isButtonVisible = !this.isParent && this.key.length > 1 && !metadataSearchDisabled.find((value) => (value === this.key));
            if (this.value) {
                this.displayValue = this.value.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
                this.isButtonEnabled = true;
                this.buttonTooltip = 'Search (in background) for ' + this.key + '::' + this.displayValue;
            }
            if (!this.keyTooltip) {
                this.keyTooltip = this.key;
            }
        }
    }
}
